import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores';

const PasswordResetConfirmPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const resetPasswordWithToken = useAuthStore((state) => state.resetPasswordWithToken);
  const loading = useAuthStore((state) => state.loading);

  const [username, setUsername] = useState('');
  const [token, setTokenValue] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setUsername(params.get('username') || '');
    setTokenValue(params.get('token') || '');
  }, [location.search]);

  const validateForm = () => {
    const newErrors = {};
    if (!username) newErrors.username = t('errors.required_field');
    if (!newPassword) newErrors.newPassword = t('errors.required_field');
    if (newPassword && newPassword.length < 8)
      newErrors.newPassword = t('errors.min_length', { count: 8 });
    if (newPassword !== confirmPassword)
      newErrors.confirmPassword = t('errors.passwords_not_match');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const result = await resetPasswordWithToken(token, newPassword);
      if (result.success) {
        setStatusMessage(t(result.message));
        setErrors({});
        setTimeout(() => navigate('/'), 2000);
      } else {
        setErrors({ submit: t(result.error) });
      }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('auth.reset_password.title', { username, defaultValue: `Hi, ${username}` })}
      </Typography>
      <form onSubmit={handleSubmit}>
        <input type="hidden" name="token" value={token} />
        <TextField
          label={t('auth.reset_password.new_password', 'new password')}
          name="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.newPassword}
          helperText={errors.newPassword}
        />
        <TextField
          label={t('auth.register.confirm_password', 'confirm password')}
          name="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
        />
        {errors.submit && (
          <Typography color="error" sx={{ mt: 1 }}>
            {errors.submit}
          </Typography>
        )}
        {statusMessage && (
          <Typography color="success.main" sx={{ mt: 1 }}>
            {statusMessage}
          </Typography>
        )}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          {loading
            ? t('common.loading')
            : t('auth.reset_password.submit', 'Reset password')}
        </Button>
      </form>
    </Box>
  );
};

export default PasswordResetConfirmPage;
